import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Wrench, Search, Map, Tag, User, LayoutDashboard, LogOut } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const Brand = () => (
  <Link to="/" data-testid="nav-brand-logo" className="flex items-center gap-2 group">
    <div className="h-9 w-9 rounded-xl bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/25 group-hover:rotate-12 transition-transform">
      <Wrench size={20} className="text-slate-950" strokeWidth={2.5} />
    </div>
    <span className="font-head font-black text-2xl tracking-tight text-slate-50">
      Motor<span className="text-amber-500">Pro</span>
    </span>
  </Link>
);

export const Layout = ({ children }) => {
  const { user, logout, openAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (p) => location.pathname === p;

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100 pb-24 lg:pb-0">
      {/* Top nav */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-950/80 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <nav className="hidden lg:flex items-center gap-1">
            <Link data-testid="nav-link-find-services" to="/" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive("/") ? "text-amber-400" : "text-slate-300 hover:text-white"}`}>Find Services</Link>
            <Link data-testid="nav-link-business-pricing" to="/pricing" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive("/pricing") ? "text-amber-400" : "text-slate-300 hover:text-white"}`}>For Business</Link>
            {user && user.role === "business" && (
              <Link data-testid="nav-link-dashboard" to="/dashboard" className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${isActive("/dashboard") ? "text-amber-400" : "text-slate-300 hover:text-white"}`}>Dashboard</Link>
            )}
          </nav>
          <div className="flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-account-menu" className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors">
                    <div className="h-7 w-7 rounded-full bg-amber-500 flex items-center justify-center text-slate-950 font-bold text-sm">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <span className="text-sm font-semibold hidden sm:block">{user.name?.split(" ")[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800 text-slate-100">
                  <div className="px-2 py-1.5 text-xs text-slate-400">{user.email}<span className="ml-1 text-amber-400 uppercase">· {user.role}</span></div>
                  <DropdownMenuSeparator className="bg-slate-800" />
                  {user.role === "business" && (
                    <DropdownMenuItem onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-slate-800">
                      <LayoutDashboard size={15} className="mr-2" /> Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="nav-logout" onClick={doLogout} className="cursor-pointer focus:bg-slate-800 text-red-400">
                    <LogOut size={15} className="mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button data-testid="nav-login-button" onClick={() => openAuth("login")}
                className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-xl">
                Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <main>{children}</main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 backdrop-blur-md bg-slate-950/90 border-t border-slate-800 flex justify-around items-center py-2 px-2">
        <BottomLink to="/" testid="mobile-bottom-nav-search" icon={Search} label="Find" active={isActive("/")} />
        <BottomLink to="/?view=map" testid="mobile-bottom-nav-map" icon={Map} label="Map" active={location.search.includes("map")} />
        <BottomLink to="/pricing" testid="mobile-bottom-nav-pricing" icon={Tag} label="Pricing" active={isActive("/pricing")} />
        {user && user.role === "business" ? (
          <BottomLink to="/dashboard" testid="mobile-bottom-nav-account" icon={LayoutDashboard} label="Business" active={isActive("/dashboard")} />
        ) : (
          <button data-testid="mobile-bottom-nav-account" onClick={() => (user ? navigate("/") : openAuth("login"))}
            className="flex flex-col items-center gap-0.5 px-3 py-1 text-slate-400">
            <User size={20} /><span className="text-[10px] font-semibold">{user ? "Account" : "Sign in"}</span>
          </button>
        )}
      </nav>
    </div>
  );
};

const BottomLink = ({ to, testid, icon: Icon, label, active }) => (
  <Link to={to} data-testid={testid}
    className={`flex flex-col items-center gap-0.5 px-3 py-1 transition-colors ${active ? "text-amber-400" : "text-slate-400"}`}>
    <Icon size={20} /><span className="text-[10px] font-semibold">{label}</span>
  </Link>
);
